/**
 * The Rikked tab of Kliendiotsing: the lead watch (R9) as the tab's engine
 * strip, and under it the open incidents an alert call may be placed on.
 *
 * It was a card above the call queue, and moved here in plan step 3: the
 * incidents are this engine's rows, so the engine sits on the tab that lists
 * them, the same way the scan autopilot sits over the scans. Its name changed
 * with the move, "Kodulehtede valve" to "Vihjete valve", so it cannot be read
 * as Saidiseire, which watches the sites of clients who pay us. In plan step 5
 * the card became the one-line strip all three engines share
 * (`prospecting/engines/EngineLine.tsx`): the numbers and the settings form
 * are behind its gear, so the incidents are the first list on the tab.
 *
 * THE OFF STATE HAS TO EXPLAIN ITSELF. The watch ships switched off and is off
 * in production, and a tab of its own promotes it to a step of the funnel. A
 * strip that only said "Off." above a list of defects would leave the owner
 * guessing where the defects came from and what switching it on would start.
 * So while it is off the strip carries a note under its line: what the watch
 * would do, where today's rows come from instead (scans and the brain), and
 * who may switch it on - the switch itself is drawn only for
 * `manage:ariregister`, the key the server checks.
 *
 * The switch writes straight through, like the autopilots', because a stop
 * button with a save step is not a stop button. While it is on, the line says
 * when a homepage was last checked and whether our own network answered the
 * last time it asked; how many homepages it holds is behind the gear.
 *
 * This file is the tab and the watch's strip; the settings form lives in
 * `WatchSettings.tsx`, the incident card in `IncidentList.tsx` and the state in
 * `useSiteWatch.ts`.
 */
import { Loader2, Radar, RefreshCw } from "lucide-react";
import { cn } from "@wa/ui";
import { useI18n } from "../../../../i18n/I18nProvider";
import { formatWhen } from "../../../../lib/siteScan";
import { dateLocale, networkState, type SiteWatchState } from "../../../../lib/siteWatch";
import { Btn } from "../../../ui";
import { EngineLine, EngineStat } from "../../prospecting/engines";
import type { T } from "../../site-scanner/types";
import { IncidentList } from "./IncidentList";
import { WatchSettings } from "./WatchSettings";
import { useSiteWatch, type WatchNotice } from "./useSiteWatch";

function noticeText(notice: WatchNotice, t: T): string {
  if (notice.kind === "refreshed") return t("leads.watch.notice.refreshed", { n: notice.watched });
  if (notice.kind === "saveFailed") return t("leads.watch.notice.saveFailed");
  return t("leads.watch.notice.refreshFailed");
}

function statusLine(state: SiteWatchState, dates: string, t: T): string {
  const net = networkState(state.network);
  const when = state.network ? formatWhen(state.network.at, dates) : "";
  const network =
    net === "ok"
      ? t("leads.watch.network.ok", { when })
      : net === "down"
        ? t("leads.watch.network.down", { when })
        : t("leads.watch.network.unknown");
  const checked = state.lastCheckedAt
    ? t("leads.watch.lastChecked", { when: formatWhen(state.lastCheckedAt, dates) })
    : t("leads.watch.neverChecked");
  return `${checked} ${network}`;
}

/** What the strip says under its line while the watch is off. */
function OffState({ minutes, canManage, t }: { minutes: number; canManage: boolean; t: T }) {
  return (
    <div className="space-y-2 text-[13px] leading-snug">
      <p className="crm-sub">{t("leads.watch.offWhat", { minutes })}</p>
      <p className="crm-sub">{t("leads.watch.offNow")}</p>
      <p className="crm-sub">{canManage ? t("leads.watch.offHowTo") : t("leads.watch.offNoRight")}</p>
    </div>
  );
}

/**
 * The tab: the watch's strip, then the incidents.
 *
 * One `useSiteWatch` feeds both: it reads the watch and the open incidents side
 * by side, and a save or a list refresh re-reads the two together. The
 * incident card still draws when the watch's own read failed: the defects the
 * scanner and the brain filed do not depend on the watch at all.
 */
export function WatchPanel({ canManage, t }: { canManage: boolean; t: T }) {
  const { locale } = useI18n();
  const dates = dateLocale(locale);
  const watch = useSiteWatch();

  return (
    <>
      <WatchLine watch={watch} canManage={canManage} dates={dates} t={t} />
      <IncidentList incidents={watch.incidents} failed={watch.incidentsFailed} dates={dates} t={t} />
    </>
  );
}

function WatchLine({
  watch,
  canManage,
  dates,
  t,
}: {
  watch: ReturnType<typeof useSiteWatch>;
  canManage: boolean;
  dates: string;
  t: T;
}) {
  const state = watch.state;

  if (!state) {
    if (!watch.loadFailed) return null;
    // No switch and no gear: there is no state to switch or to show, only the
    // way to ask again.
    return (
      <EngineLine
        icon={Radar}
        title={t("leads.watch.title")}
        status={t("leads.watch.loadFailed")}
        enabled={false}
        canManage={canManage}
        actions={
          <Btn small onClick={() => void watch.reload()}>
            {t("leads.watch.retry")}
          </Btn>
        }
        settingsLabel={t("leads.watch.settings")}
      />
    );
  }

  const s = state.settings;
  const busy = watch.saving || watch.refreshing;
  const notice = watch.notice ? (
    <p
      className={cn("crm-sub text-[13px]", watch.notice.kind !== "refreshed" && "text-[var(--color-destructive)]")}
      role="status"
    >
      {noticeText(watch.notice, t)}
    </p>
  ) : null;

  return (
    <EngineLine
      icon={Radar}
      title={t("leads.watch.title")}
      status={s.enabled ? t("leads.watch.on", { minutes: s.homepageMinutes }) : t("leads.watch.off")}
      when={s.enabled ? statusLine(state, dates, t) : undefined}
      enabled={s.enabled}
      canManage={canManage}
      saving={watch.saving}
      onToggle={(checked) => void watch.patch({ enabled: checked })}
      actions={
        canManage ? (
          <Btn small disabled={busy} onClick={() => void watch.refreshList()}>
            {watch.refreshing ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
            {watch.refreshing ? t("leads.watch.refreshing") : t("leads.watch.refresh")}
          </Btn>
        ) : null
      }
      note={
        !s.enabled || notice ? (
          <div className="space-y-2">
            {s.enabled ? null : <OffState minutes={s.homepageMinutes} canManage={canManage} t={t} />}
            {notice}
          </div>
        ) : undefined
      }
      settingsLabel={t("leads.watch.settings")}
    >
      <p className="crm-sub px-[26px] pt-3 text-[12px] leading-snug">{t("leads.watch.scope")}</p>

      {/* The numbers only while it runs: an off watch holds nothing, and a
          row of zeros would read as a watch that looked and found nothing. */}
      {s.enabled ? (
        <div className="crm-pstats grid-cols-2 sm:grid-cols-3">
          <EngineStat label={t("leads.watch.stat.watched")} value={state.stats.watched} />
          <EngineStat label={t("leads.watch.stat.due")} value={state.stats.due} />
          <EngineStat label={t("leads.watch.stat.failing")} value={state.stats.failing} hot />
          <EngineStat label={t("leads.watch.stat.open")} value={state.stats.openIncidents} />
          <EngineStat label={t("leads.watch.stat.callable")} value={state.stats.callable} />
        </div>
      ) : null}

      <WatchSettings
        settings={s}
        canManage={canManage}
        saving={watch.saving}
        onPatch={watch.patch}
        t={t}
      />
    </EngineLine>
  );
}
